import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSEO } from '../hooks/useSEO';

const HelpCenter = () => {
  useSEO({
    title: "Help Center - Support & Guides | AM CARDMATE",
    description: "Find answers to common questions, tutorials, and support guides for AM CARDMATE digital business cards and NFC solutions.",
    image: "/assets/help.jpg",
   keywords: "help center, support, guides, tutorials, FAQ, troubleshooting, documentation"
  });
  useEffect(() => {
  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "AM CARDMATE Help Center",
      "description": "Find answers to common questions about digital business cards and NFC solutions",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How do I create my first digital business card?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Creating your first card is simple! Just tap the '+' button on the home screen, fill in your details, choose a design, and save."
          }
        },
        {
          "@type": "Question",
          "name": "Is AM CARDMATE free to use?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! AM CARDMATE offers a free plan that includes one digital card, basic backgrounds, and QR code sharing. Premium features are available for users who need more functionality."
          }
        },
        {
          "@type": "Question",
          "name": "How does NFC sharing work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Simply tap your phone to another NFC-enabled device. The recipient will automatically receive a notification to view your card - no app needed on their end!"
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to Use AM CARDMATE",
      "description": "Step-by-step guide to creating and sharing digital business cards",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Download the App",
          "text": "Download AM CARDMATE from App Store or Google Play",
          "url": "https://amcardmate.com/download"
        },
        {
          "@type": "HowToStep",
          "name": "Create Your Card",
          "text": "Fill in your contact details and choose a design",
          "url": "https://amcardmate.com/help#create"
        },
        {
          "@type": "HowToStep",
          "name": "Share Your Card",
          "text": "Share via NFC, QR code, or direct link",
          "url": "https://amcardmate.com/help#share"
        }
      ]
    }
  ];
  
  schemas.forEach(schema => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}, []);
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [openFaq, setOpenFaq] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const categories = [
    { id: 'getting-started', name: 'Getting Started', icon: 'rocket' },
    { id: 'account', name: 'Account & Billing', icon: 'user-cog' },
    { id: 'cards', name: 'Digital Cards', icon: 'credit-card' },
    { id: 'nfc', name: 'NFC & Sharing', icon: 'wifi' },
    { id: 'premium', name: 'Premium Features', icon: 'star' },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: 'tools' }
  ];

  const faqs = {
    'getting-started': [
      {
        question: 'How do I create my first digital business card?',
        answer: 'Creating your first card is simple! Just tap the "+" button on the home screen, fill in your details, choose a design, and save. Your card will be instantly ready to share.',
        video: true
      },
      {
        question: 'Is AM CARDMATE free to use?',
        answer: 'Yes! AM CARDMATE offers a free plan that includes one digital card, basic backgrounds, and QR code sharing. Premium features are available for users who need more functionality.',
        helpful: true
      },
      {
        question: 'What devices are supported?',
        answer: 'AM CARDMATE is available on iOS (iPhone 11 and later) and Android devices with NFC capability. The app requires iOS 13+ or Android 8.0+.',
        helpful: false
      },
      {
        question: 'How do I get started?',
        answer: 'Download AM CARDMATE from the App Store or Google Play, create your account, and follow the simple setup wizard to create your first digital business card.',
        helpful: true
      }
    ],
    'account': [
      {
        question: 'How do I manage my subscription?',
        answer: 'You can manage your subscription directly through your device\'s app store. For iOS, go to Settings > Your Name > Subscriptions. For Android, open Google Play Store > Menu > Subscriptions.',
        video: true
      },
      {
        question: 'Can I cancel my subscription anytime?',
        answer: 'Yes, you can cancel your subscription at any time. You\'ll continue to have access to premium features until the end of your current billing period.',
        helpful: true
      },
      {
        question: 'Do you offer refunds?',
        answer: 'Digital subscriptions follow the refund policies of Apple App Store and Google Play Store. Physical products (NFC cards) are non-refundable once shipped.',
        helpful: false
      },
      {
        question: 'How do I update my payment method?',
        answer: 'Payment methods are managed through your app store account. Update your payment information in your Apple ID or Google Play account settings.',
        helpful: true
      }
    ],
    'cards': [
      {
        question: 'How many cards can I create?',
        answer: 'Free users can create 1 card. Premium users can create up to 20 different cards for various purposes (personal, business, events, etc.).',
        helpful: true
      },
      {
        question: 'Can I edit my card after creating it?',
        answer: 'Yes! You can edit your card details, design, and information at any time. Changes are saved instantly and reflected immediately when shared.',
        video: true
      },
      {
        question: 'How do I delete a card?',
        answer: 'To delete a card, go to your card list, swipe left on the card you want to delete (or tap the three dots menu), and select "Delete". Note: This action cannot be undone.',
        helpful: false
      },
      {
        question: 'Can I duplicate a card?',
        answer: 'Premium users can duplicate existing cards to create variations quickly. This is perfect for creating event-specific or campaign-specific versions.',
        helpful: true
      }
    ],
    'nfc': [
      {
        question: 'How does NFC sharing work?',
        answer: 'Simply tap your phone to another NFC-enabled device. The recipient will automatically receive a notification to view your card - no app needed on their end!',
        video: true
      },
      {
        question: 'What if someone doesn\'t have NFC?',
        answer: 'No problem! You can share via QR code, link, or add to contacts directly. AM CARDMATE offers 5 different sharing methods to ensure compatibility.',
        helpful: true
      },
      {
        question: 'Can I program physical NFC cards?',
        answer: 'Yes! Premium users can use our built-in NFC writer to program physical NFC tags or cards with their digital business card information.',
        helpful: true
      },
      {
        question: 'Is NFC sharing secure?',
        answer: 'Absolutely! NFC sharing only transmits your card\'s public URL. All data is encrypted, and you control exactly what information is visible on your card.',
        helpful: true
      }
    ],
    'premium': [
      {
        question: 'What\'s included in Premium?',
        answer: 'Premium includes: up to 20 cards, analytics dashboard, multiple collections, custom backgrounds, NFC writer access, verified badge, and priority support.',
        helpful: true
      },
      {
        question: 'Is there a free trial?',
        answer: 'We occasionally offer free trials for new users. Check the app for current promotions. All subscriptions can be cancelled anytime.',
        helpful: false
      },
      {
        question: 'Do you offer team/enterprise plans?',
        answer: 'Yes! Enterprise plans are coming in 2026 with bulk pricing, team management dashboard, and centralized billing. Contact us for early access.',
        helpful: true
      },
      {
        question: 'What\'s the difference between monthly and annual plans?',
        answer: 'Annual plans offer significant savings (up to 33% off) compared to monthly billing. You also get priority access to new features with annual plans.',
        helpful: true
      }
    ],
    'troubleshooting': [
      {
        question: 'NFC isn\'t working on my device',
        answer: 'Ensure NFC is enabled in your phone settings. For iPhone, it\'s always on for iPhone 11+. For Android, check Settings > Connected devices > Connection preferences > NFC.',
        helpful: true
      },
      {
        question: 'I can\'t see my analytics',
        answer: 'Analytics is a premium feature. Ensure you have an active premium subscription. Data may take up to 5 minutes to update after someone views your card.',
        helpful: false
      },
      {
        question: 'The app is crashing or freezing',
        answer: 'Try these steps: 1) Force close and restart the app, 2) Check for app updates, 3) Restart your device, 4) If issues persist, contact support with your device model and iOS/Android version.',
        helpful: true
      },
      {
        question: 'My changes aren\'t saving',
        answer: 'Ensure you have a stable internet connection. Changes are saved to the cloud automatically. If problems persist, try logging out and back in.',
        helpful: true
      }
    ]
  };

  const quickLinks = [
    { icon: 'book', title: 'User Guide', desc: 'Complete documentation', link: '/guide' },
    { icon: 'video', title: 'Video Tutorials', desc: 'Coming Soon', link: '#videos', isComingSoon: true },
    { icon: 'headset', title: 'Contact Support', desc: '24/7 assistance', link: '/contact' }
  ];


  return (
    <>
      <section className="pt-20 md:pt-24 lg:pt-32 pb-32 bg-gradient-to-br from-white via-blue-50/30 to-white relative overflow-hidden">
        <div className="absolute top-10 right-10 w-[800px] h-[800px] bg-gradient-to-br from-blue-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl" />
        <div className="absolute -bottom-40 -left-20 w-[600px] h-[600px] bg-gradient-to-tr from-purple-200/20 via-blue-200/20 to-transparent rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div 
              className="badge badge-blue inline-flex mb-6"
              whileHover={{ scale: 1.05 }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <FontAwesomeIcon icon="life-ring" />
              <span>Help Center</span>
            </motion.div>
            
            <h1 className="text-h1 mb-6">
              How Can We <span className="text-gradient-blue-pink">Help You Today?</span>
            </h1>
            
            <p className="text-body-lg text-gray max-w-3xl mx-auto mb-12 leading-relaxed">
              Find <span className="text-gradient-blue-pink font-semibold">instant answers</span>, explore comprehensive guides, 
              or connect with our <span className="text-gradient-blue-pink font-semibold">support team</span> available 24/7.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-purple-50/20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <div className="badge badge-green inline-flex mb-6">
                  <FontAwesomeIcon icon="award" />
                  <span>Excellence in Support</span>
                </div>
                
                <h2 className="text-h2 text-dark mb-6">
                  We're Here for Your <span className="text-gradient-blue-pink">Success</span>
                </h2>
                
                <p className="text-body-lg text-gray mb-8 leading-relaxed">
                  Our dedicated support team is committed to making your experience with AM CARDMATE 
                  seamless and successful. From quick answers to detailed guidance, we've got you covered.
                </p>
                
                <div className="space-y-6 mb-8">
                  {[
                    { 
                      icon: 'clock',
                      title: 'Lightning Fast Response',
                      desc: 'Average response time under 2 hours for premium users'
                    },
                    {
                      icon: 'globe',
                      title: 'Global Support Team',
                      desc: 'Available 24/7 across all timezones'
                    },
                    {
                      icon: 'star',
                      title: '98% Satisfaction Rate',
                      desc: 'Based on thousands of support interactions'
                    },
                    {
                      icon: 'users',
                      title: 'Community Driven',
                      desc: 'Active community helping each other succeed'
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                        <FontAwesomeIcon icon={item.icon} />
                      </div>
                      <div>
                        <h4 className="text-h5 text-dark mb-1">{item.title}</h4>
                        <p className="text-body-sm text-gray">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <motion.a 
                    href="/contact"
                    className="btn btn-primary inline-flex"
                    whileHover={{ scale: 1.05 }}
                  >
                    <FontAwesomeIcon icon="headset" />
                    <span>Contact Support</span>
                  </motion.a>
                </div>
              </div>
              
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="/assets/image34.png" 
                    alt="Support Excellence" 
                    className="w-full h-full object-cover"
                  />
                  
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
                    <div className="grid grid-cols-3 gap-4 text-white text-center">
                      <div>
                        <div className="text-h4">Endless</div>
                        <div className="text-body-sm opacity-80">Happy Users</div>
                      </div>
                      <div>
                        <div className="text-h4">24/7</div>
                        <div className="text-body-sm opacity-80">Support</div>
                      </div>
                      <div>
                        <div className="text-h4">2hrs</div>
                        <div className="text-body-sm opacity-80">Avg Response</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl px-4 py-2 shadow-xl">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon="check-circle" className="text-green-500" />
                    <span className="text-body-sm font-semibold">98% Satisfied</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {quickLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.link}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all text-center group relative overflow-hidden"
                whileHover={{ y: -10 }}
                onClick={(e) => link.isComingSoon && setShowVideoModal(true) && e.preventDefault()}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <motion.div 
                  className={`w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg relative`}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <FontAwesomeIcon icon={link.icon} className="text-2xl" />
                  {link.isComingSoon && (
                    <div className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                      Soon
                    </div>
                  )}
                </motion.div>
                <h3 className="text-h5 text-dark mb-1">{link.title}</h3>
                <p className="text-body-sm text-gray">{link.desc}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-purple-50/20 to-white relative overflow-hidden">
        <div className="absolute top-[20%] left-[10%] w-[500px] h-[500px] bg-gradient-to-br from-blue-200/20 via-purple-200/20 to-transparent rounded-full filter blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12">
            <div className="badge badge-blue inline-flex mb-6">
              <FontAwesomeIcon icon="question-circle" />
              <span>Browse Topics</span>
            </div>
            
            <h2 className="text-h1 mb-6">
              Find <span className="text-gradient-blue-pink">Answers</span> by Category
            </h2>
            <p className="text-body-lg text-gray">
              Everything you need to know about <span className="text-gradient-blue-pink font-semibold">AM CARDMATE</span>
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-3 rounded-full font-medium transition-all text-body-sm ${
                    activeCategory === category.id
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 shadow-md hover:shadow-lg'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FontAwesomeIcon icon={category.icon} className="mr-2" />
                  {category.name}
                </motion.button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeCategory}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="space-y-4"
                  >
                    {faqs[activeCategory].map((faq, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
                      >
                        <button
                          onClick={() => setOpenFaq(openFaq === index ? null : index)}
                          className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 transition-all"
                        >
                          <div className="flex items-center gap-4 flex-1">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-600 rounded-lg flex items-center justify-center text-white flex-shrink-0">
                              <FontAwesomeIcon icon="question" className="text-sm" />
                            </div>
                            <h3 className="text-h5 text-dark pr-4">{faq.question}</h3>
                          </div>
                          <motion.div
                            animate={{ rotate: openFaq === index ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="flex-shrink-0"
                          >
                            <FontAwesomeIcon icon="chevron-down" className="text-gray-400" />
                          </motion.div>
                        </button>
                        
                        <AnimatePresence>
                          {openFaq === index && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <div className="px-6 pb-6">
                                <div className="pl-12">
                                  <p className="text-body text-gray leading-relaxed mb-4">{faq.answer}</p>
                                  
                                  {faq.video && (
                                    <motion.button
                                      className="flex items-center gap-2 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 font-medium text-body-sm mb-4"
                                      onClick={() => setShowVideoModal(true)}
                                      whileHover={{ x: 5 }}
                                    >
                                      <FontAwesomeIcon icon="play-circle" className="text-blue-600" />
                                      Watch Video Tutorial (Coming Soon)
                                    </motion.button>
                                  )}
                                  
                                  <div className="pt-4 border-t border-gray-100">
                                    <p className="text-body-sm text-gray mb-3">Was this helpful?</p>
                                    <div className="flex gap-3">
                                      <motion.button 
                                        className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium text-body-sm shadow-md hover:shadow-lg transition-all"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                      >
                                        <FontAwesomeIcon icon="thumbs-up" className="mr-2" />
                                        Yes
                                      </motion.button>
                                      <motion.button 
                                        className="px-4 py-2 bg-gray-100 text-gray rounded-lg font-medium text-body-sm hover:bg-gray-200 transition-all"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                      >
                                        <FontAwesomeIcon icon="thumbs-down" className="mr-2" />
                                        No
                                      </motion.button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 shadow-lg">
                    <h3 className="text-h5 text-dark mb-4">
                      Need More <span className="text-gradient-blue-pink">Help?</span>
                    </h3>
                    <div className="space-y-3">
                      <motion.a 
                        href="mailto:support@amcardmate.com"
                        className="flex items-center gap-3 text-gray hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-blue-600 hover:to-purple-600 transition-all text-body-sm"
                        whileHover={{ x: 5 }}
                      >
                        <FontAwesomeIcon icon="envelope" className="text-blue-500" />
                        <span className="font-medium">Email Support</span>
                      </motion.a>
                      <motion.a 
                        href="#"
                        className="flex items-center gap-3 text-gray hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-green-600 hover:to-emerald-600 transition-all text-body-sm"
                        whileHover={{ x: 5 }}
                      >
                        <FontAwesomeIcon icon="comments" className="text-green-500" />
                        <span className="font-medium">Quick Chat</span>
                      </motion.a>
                      <motion.a 
                        href="#"
                        className="flex items-center gap-3 text-gray hover:text-transparent hover:bg-clip-text hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 transition-all text-body-sm"
                        whileHover={{ x: 5 }}
                      >
                        <FontAwesomeIcon icon="book" className="text-purple-500" />
                        <span className="font-medium">User Guide</span>
                      </motion.a>
                    </div>
                  </div>

                  <motion.div 
                    className="bg-white rounded-2xl p-6 shadow-lg"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FontAwesomeIcon icon="clock" className="text-3xl text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600 mb-3" />
                    <h3 className="text-h5 text-dark mb-2">
                      <span className="text-gradient-blue-pink">Fast</span> Response Times
                    </h3>
                    <div className="space-y-2 text-body-sm">
                      <div className="flex justify-between">
                        <span className="text-gray">Premium Support:</span>
                        <span className="font-semibold text-green-600">2-4 hours</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray">Standard Support:</span>
                        <span className="font-semibold text-blue-600">24 hours</span>
                      </div>
                    </div>
                  </motion.div>

                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 shadow-lg">
                    <h3 className="text-h5 text-dark mb-4">Direct Contact</h3>
                    <p className="text-body-sm text-gray mb-4">
                      Can't find what you're looking for? Get direct help from our team.
                    </p>
                    <motion.a 
                      href="/contact"
                      className="btn btn-primary btn-sm w-full"
                      whileHover={{ scale: 1.02 }}
                    >
                      <FontAwesomeIcon icon="headset" />
                      <span>Contact Support</span>
                    </motion.a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="badge badge-pink inline-flex mb-6">
              <FontAwesomeIcon icon="video" />
              <span>Video Guides</span>
              <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold ml-2">
                Coming Soon
              </span>
            </div>
            
            <h2 className="text-h2 mb-4">
              Learn with <span className="text-gradient-blue-pink">Video Tutorials</span>
            </h2>
            
            <p className="text-body-lg text-gray">
              Visual step-by-step guides to help you master <span className="font-semibold text-gradient-blue-pink">AM CARDMATE</span>
            </p>
            <p className="text-body-sm text-gray mt-2">
              We're creating comprehensive video tutorials. They'll be available soon!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { title: 'Getting Started Guide', views: 'Soon', category: 'Basics' },
              { title: 'Creating Your First Card', views: 'Soon', category: 'Cards' },
              { title: 'Using NFC Features',  views: 'Soon', category: 'Advanced' },
              { title: 'Analytics Dashboard',  views: 'Soon', category: 'Premium' },
              { title: 'Managing Contacts', views: 'Soon', category: 'Features' },
              { title: 'Customizing Cards',  views: 'Soon', category: 'Design' }
            ].map((video, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all group cursor-pointer relative"
                whileHover={{ y: -5 }}
                onClick={() => setShowVideoModal(true)}
              >
                <div className="relative h-48 bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center">
                  <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                    <div className="text-center">
                      <motion.div 
                        className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform"
                      >
                        <FontAwesomeIcon icon="play" className="text-white text-2xl ml-1" />
                      </motion.div>
                      <span className="text-white text-body-sm font-semibold">Coming Soon</span>
                    </div>
                  </div>
                  <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                    {video.category}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-h5 text-dark mb-2">{video.title}</h3>
                  <p className="text-body-sm text-gray flex items-center gap-2">
                    <FontAwesomeIcon icon="clock" className="text-xs" />
                    Available Soon
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-blue-pink text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-15"></div>
        <div className="absolute top-20 left-10 w-24 h-24 rounded-full bg-white opacity-20"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-white opacity-10"></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 rounded-full bg-white opacity-15"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-h2 mb-6">
              Still Need <span className="text-white">Help?</span>
            </h2>
            
            <p className="text-body-lg text-white/90 mb-12 leading-relaxed">
              Our <span className="font-semibold">global support team</span> is ready to assist you with any questions or issues
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <motion.a
                href="/contact"
                className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/30 transition-all group"
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                  <FontAwesomeIcon icon="envelope" className="text-2xl" />
                </div>
                <h3 className="text-h5 mb-1 text-white">Email Support</h3>
                <p className="text-body-sm text-white/80">Get help via email</p>
              </motion.a>
              
              <motion.div
                className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/30 transition-all group"
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                  <FontAwesomeIcon icon="comments" className="text-2xl" />
                </div>
                <h3 className="text-h5 mb-1 text-white">Quick Chat</h3>
                <p className="text-body-sm text-white/80">Fast Assistance</p>
              </motion.div>
              
              <motion.a
                href="/contact"
                className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/30 transition-all group"
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-white mx-auto mb-4">
                  <FontAwesomeIcon icon="phone" className="text-2xl" />
                </div>
                <h3 className="text-h5 mb-1 text-white">Call Back</h3>
                <p className="text-body-sm text-white/80">Request a call</p>
              </motion.a>
            </div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {showVideoModal && (
          <motion.div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div 
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                  <FontAwesomeIcon icon="play-circle" className="text-white text-3xl" />
                  <div className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                    Soon
                  </div>
                </div>
                
                <h3 className="text-h3 text-dark mb-4">
                  Video Tutorials <span className="text-gradient-blue-pink">Coming Soon!</span>
                </h3>
                <p className="text-body text-gray mb-6">
                  We're creating comprehensive video guides to help you make the most of AM CARDMATE. 
                  They'll be ready very soon!
                </p>
                
                <motion.button 
                  className="btn btn-primary"
                  onClick={() => setShowVideoModal(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Got it!
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};


export default HelpCenter;